import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Card, CardHeader } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { admin as adminApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { AuditEntry } from "@/lib/types";

/** The audit log viewer (docs/08 §8.11). */
export function AuditLogView() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ table_name: "", action: "", date_from: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(
        await adminApi.audit({
          table_name: filters.table_name || undefined,
          action: filters.action || undefined,
          date_from: filters.date_from || undefined,
          limit: 200,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">{strings.admin.auditLog}</h1>

      <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
        The audit log is append-only. Nobody, including an administrator, can
        change or delete an entry: the database itself refuses.
      </p>

      <div className="flex flex-wrap gap-2">
        <Select
          aria-label="Table"
          placeholder="Everything"
          value={filters.table_name}
          onChange={(event) => setFilters({ ...filters, table_name: event.target.value })}
          options={[
            { value: "isolates", label: "Isolates" },
            { value: "users", label: "Users" },
            { value: "qa_flags", label: "QA flags" },
            { value: "exports", label: "Exports" },
            { value: "sessions", label: "Sessions" },
          ]}
        />
        <Select
          aria-label="Action"
          placeholder="Any action"
          value={filters.action}
          onChange={(event) => setFilters({ ...filters, action: event.target.value })}
          options={[
            { value: "insert", label: "Created" },
            { value: "update", label: "Changed" },
            { value: "delete_soft", label: "Voided" },
            { value: "resolve_flag", label: "Flag resolved" },
            { value: "export", label: "Exported" },
            { value: "login", label: "Signed in" },
          ]}
        />
        <Input
          type="date"
          aria-label="From"
          value={filters.date_from}
          onChange={(event) => setFilters({ ...filters, date_from: event.target.value })}
        />
      </div>

      <Card>
        <CardHeader title={`${entries.length} entries`} />
        {loading ? (
          <div className="p-8">
            <Spinner label="Loading the audit log" />
          </div>
        ) : entries.length === 0 ? (
          <EmptyState title="Nothing matches those filters." />
        ) : (
          <Table caption="Audit log">
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Table</Th>
                <Th>Changes</Th>
                <Th>From</Th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(entry.occurred_at)}
                  </Td>
                  <Td>
                    {entry.actor_name ?? "system"}
                    {entry.actor_role && (
                      <Badge className="ml-1">{entry.actor_role.replace(/_/g, " ")}</Badge>
                    )}
                  </Td>
                  <Td>{entry.action.replace(/_/g, " ")}</Td>
                  <Td className="font-mono text-xs">{entry.table_name}</Td>
                  <Td className="max-w-md">
                    {entry.changes ? (
                      <ul className="space-y-0.5 text-xs">
                        {Object.entries(entry.changes)
                          .slice(0, 5)
                          .map(([field, change]) => (
                            <li key={field} className="truncate">
                              <span className="font-medium">{field}</span>:{" "}
                              <span className="text-red-700">{JSON.stringify(change.old)}</span>{" "}
                              &rarr;{" "}
                              <span className="text-green-700">{JSON.stringify(change.new)}</span>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td className="font-mono text-xs text-slate-500">
                    {entry.ip_address ?? "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
